import { FC, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Image } from '~/components/wrappers/Image';
import { shopifySrcSet } from '~/common/utils/image';
import { ProductMedia } from '~/types/product';

interface DesktopImageGalleryProps {
  media: ProductMedia[];
  productTitle: string;
  currentIndex: number;
  onImageRef: (index: number, el: HTMLDivElement | null) => void;
}

interface DesktopMediaItemProps {
  media: ProductMedia;
  index: number;
  currentIndex: number;
  productTitle: string;
  isFirst: boolean;
}

const DesktopMediaItem: FC<DesktopMediaItemProps> = ({
  media,
  index,
  currentIndex,
  productTitle,
  isFirst,
}) => {
  const slotRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [shouldMountVideo, setShouldMountVideo] = useState(false);
  const [userPaused, setUserPaused] = useState(false);

  useEffect(() => {
    if (media.kind !== 'video') return;
    const el = slotRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldMountVideo(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200% 0px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [media.kind]);

  useEffect(() => {
    if (media.kind !== 'video') return;
    if (currentIndex !== index && userPaused) {
      setUserPaused(false);
    }
  }, [currentIndex, index, media.kind, userPaused]);

  useEffect(() => {
    if (media.kind !== 'video') return;
    const video = videoRef.current;
    if (!video) return;
    if (currentIndex === index && !userPaused) {
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  }, [currentIndex, index, media.kind, shouldMountVideo, userPaused]);

  const handleVideoClick = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video.play().catch(() => {});
      setUserPaused(false);
    } else {
      video.pause();
      setUserPaused(true);
    }
  };

  if (media.kind === 'image') {
    return (
      <MainImage
        src={`${media.src}&width=1200&format=auto&quality=100`}
        srcSet={shopifySrcSet(media.src, [600, 800, 1200])}
        sizes="min(70vh, 50vw)"
        alt={media.altText ?? `${productTitle} - Product image ${index + 1}`}
        loading={isFirst ? 'eager' : 'lazy'}
      />
    );
  }

  const posterUrl = media.previewImage;
  const posterSrc = posterUrl
    ? `${posterUrl}&width=1200&format=auto&quality=100`
    : undefined;

  return (
    <VideoSlot ref={slotRef}>
      {!shouldMountVideo && posterSrc && posterUrl && (
        <MainImage
          src={posterSrc}
          srcSet={shopifySrcSet(posterUrl, [600, 800, 1200])}
          sizes="min(70vh, 50vw)"
          alt={media.altText ?? `${productTitle} - Product video ${index + 1}`}
          loading={isFirst ? 'eager' : 'lazy'}
        />
      )}
      {shouldMountVideo && (
        <MainVideo
          ref={videoRef}
          playsInline
          muted
          loop
          preload="metadata"
          poster={posterSrc}
          onClick={handleVideoClick}
          aria-label={
            media.altText ?? `${productTitle} - Product video ${index + 1}`
          }
        >
          {media.sources.map((source) => (
            <source key={source.url} src={source.url} type={source.mimeType} />
          ))}
        </MainVideo>
      )}
    </VideoSlot>
  );
};

export const DesktopImageGallery: FC<DesktopImageGalleryProps> = ({
  media,
  productTitle,
  currentIndex,
  onImageRef,
}) => {
  return (
    <DesktopImageScroll>
      {media.map((item, index) => (
        <DesktopImageItem key={item.id} ref={(el) => onImageRef(index, el)}>
          <DesktopMediaItem
            media={item}
            index={index}
            currentIndex={currentIndex}
            productTitle={productTitle}
            isFirst={index === 0}
          />
        </DesktopImageItem>
      ))}
    </DesktopImageScroll>
  );
};

const DesktopImageScroll = styled.div`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;

  @media ${({ theme }) => theme.breakpoints.sm} {
    display: none;
  }
`;

const DesktopImageItem = styled.div`
  height: 100vh;
  min-height: 100vh;
  width: 100%;
  padding: 0;
  overflow: hidden;
`;

const MainImage = styled(Image)`
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
`;

const VideoSlot = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MainVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
  cursor: pointer;
`;
